import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, TrendingUp, Headphones, Calendar, Award, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Stats = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [journalStats, setJournalStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [gamifRes, journalRes] = await Promise.all([
        axios.get(`${API_URL}/gamification/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/journal/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(gamifRes.data);
      setJournalStats(journalRes.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-500 font-sans">Chargement...</p>
      </div>
    );
  }

  const themesCompleted = stats?.progress?.themes_completed || 0;
  const progressPercent = Math.round((themesCompleted / 11) * 100) || 0;

  return (
    <div className="min-h-screen pb-24 bg-sand-50" data-testid="stats-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-sage-500 to-sage-600 px-6 py-8 text-white">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="text-white hover:bg-white/10 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-3xl font-serif font-light mb-2">
          Vos statistiques
        </h1>
        <p className="text-white/80 font-sans">Votre progression personnelle</p>
      </div>

      {/* Progression globale */}
      <div className="px-6 py-6">
        <div className="bg-white rounded-2xl border border-sand-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-sage-700 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-declic" />
              Progression du programme
            </h3>
            <span className="text-2xl font-serif text-sage-700">{progressPercent}%</span>
          </div>
          
          <div className="h-4 bg-sage-100 rounded-full overflow-hidden mb-3">
            <div 
              className="h-full bg-gradient-to-r from-sage-500 to-declic rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          
          <div className="flex justify-between text-sm text-sage-500">
            <span>{stats?.progress.themes_completed || 0} thèmes complétés</span>
            <span>sur 11</span>
          </div>
        </div>
      </div>

      {/* Stats détaillées */}
      <div className="px-6 pb-4 grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-sand-200 p-4">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-3">
            <Calendar className="w-5 h-5 text-amber-600" />
          </div>
          <p className="text-2xl font-serif text-sage-700">{stats?.streak.current || 0}</p>
          <p className="text-xs text-sage-500">{stats?.streak.current > 0 ? 'Jours consécutifs' : 'Écoutez une séance pour commencer'}</p>
        </div>
        
        <div className="bg-white rounded-xl border border-sand-200 p-4">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
            <Award className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-2xl font-serif text-sage-700">{stats?.streak.max || 0}</p>
          <p className="text-xs text-sage-500">Record personnel</p>
        </div>
        
        <div className="bg-white rounded-xl border border-sand-200 p-4">
          <div className="w-10 h-10 bg-sage-100 rounded-lg flex items-center justify-center mb-3">
            <Headphones className="w-5 h-5 text-sage-600" />
          </div>
          <p className="text-2xl font-serif text-sage-700">
            {stats?.progress.themes_listened || 0}
          </p>
          <p className="text-xs text-sage-500">{stats?.progress.themes_listened > 0 ? 'Séances écoutées' : 'Votre première séance vous attend'}</p>
        </div>
        
        <div className="bg-white rounded-xl border border-sand-200 p-4">
          <div className="w-10 h-10 bg-declic/10 rounded-lg flex items-center justify-center mb-3">
            <BookOpen className="w-5 h-5 text-declic" />
          </div>
          <p className="text-2xl font-serif text-sage-700">{stats?.progress.chapters_read?.length || 0}</p>
          <p className="text-xs text-sage-500">Chapitres lus</p>
        </div>
      </div>

      {/* Badges */}
      {stats?.badges && stats.badges.length > 0 && (
        <div className="px-6 pb-4">
          <div className="bg-white rounded-xl border border-sand-200 p-5">
            <h3 className="font-medium text-sage-700 mb-4 flex items-center">
              <Award className="w-5 h-5 mr-2 text-amber-500" />
              Vos badges ({stats.badges.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {stats.badges.map((badge, index) => (
                <div key={index} className="bg-amber-50 px-3 py-2 rounded-lg">
                  <span className="text-lg mr-1">{badge.icon}</span>
                  <span className="text-sm text-amber-700">{badge.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Journal stats */}
      {journalStats && journalStats.total_entries > 0 && (
        <div className="px-6 pb-4">
          <div className="bg-white rounded-xl border border-sand-200 p-5">
            <h3 className="font-medium text-sage-700 mb-4">
              Journal de bord
            </h3>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sage-500 text-sm">{journalStats.total_entries} entrées</span>
              <span className="text-sage-500 text-sm">{journalStats.entries_this_week} cette semaine</span>
            </div>
            
            {journalStats.mood_distribution.length > 0 && (
              <div>
                <p className="text-xs text-sage-500 mb-2">Vos ressentis les plus fréquents</p>
                <div className="flex space-x-4">
                  {journalStats.mood_distribution.slice(0, 4).map(([mood, count], index) => (
                    <div key={index} className="text-center">
                      <span className="text-2xl">{mood}</span>
                      <p className="text-xs text-sage-500 mt-1">×{count}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Message motivation */}
      <div className="px-6 pb-6">
        <div className="bg-gradient-to-r from-sage-500/10 to-declic/10 rounded-xl p-5 text-center">
          <p className="text-sage-700 font-sans">
            {progressPercent >= 100 
              ? "🎉 Félicitations ! Vous avez complété le programme !"
              : progressPercent >= 50 
                ? "💪 Vous êtes à mi-chemin, continuez !"
                : "🌱 Chaque écoute vous rapproche de votre objectif"
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default Stats;
